/**
 * Matching the mesh files a URDF asks for against the files the user supplied.
 *
 * Split out of RegisterRobotModal.tsx so the matching rules are reachable
 * without rendering the modal: which candidate wins when several files share a
 * basename is the part that decides whether an upload comes out whole.
 */

import type { MeshRef } from "./urdfAssets";

/** Group the supplied files by lower-cased basename. */
export function indexFilesByName(files: File[]): Map<string, File[]> {
  const filesByName = new Map<string, File[]>();
  for (const f of files) {
    const name = f.name.toLowerCase();
    if (!filesByName.has(name)) filesByName.set(name, []);
    filesByName.get(name)!.push(f);
  }
  return filesByName;
}

/**
 * Fill in `matched`/`matchedFile` for every mesh a supplied file can satisfy.
 *
 * Meshes already matched are re-evaluated, so a later upload of a better-placed
 * file replaces an earlier basename-only guess. Meshes with no candidate are
 * returned untouched.
 */
export function matchMeshesToFiles(meshes: MeshRef[], files: File[]): MeshRef[] {
  const filesByName = indexFilesByName(files);
  return meshes.map((mesh) => {
    const candidates = filesByName.get(mesh.filename.toLowerCase());
    if (candidates && candidates.length > 0) {
      // Prefer path-based matching if webkitRelativePath is available
      let bestMatch = candidates[0];
      for (const c of candidates) {
        if (
          c.webkitRelativePath &&
          c.webkitRelativePath.includes(mesh.relativePath.replace(/^meshes\//, ""))
        ) {
          bestMatch = c;
          break;
        }
      }
      return { ...mesh, matched: true, matchedFile: bestMatch };
    }
    return mesh;
  });
}
